import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Send, Inbox as InboxIcon, CalendarClock, ExternalLink, UserPlus } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useTranslation } from "react-i18next";
import api, { apiError } from "../lib/api";

export default function MessagesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const listEnd = useRef(null);

  async function loadThreads() {
    try { const { data } = await api.get("/messages/threads"); setThreads(data || []); }
    catch (e) { toast.error(apiError(e)); }
  }
  async function loadThread(patientUserId) {
    try {
      const { data } = await api.get(`/messages/threads/${patientUserId}`);
      setThread(data || []);
      setTimeout(() => listEnd.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(apiError(e)); }
  }
  useEffect(() => {
    loadThreads();
    const iv = setInterval(loadThreads, 15000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => { if (active) loadThread(active.patient_user_id); }, [active]);

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${active.patient_user_id}/reply`, { message: reply });
      setReply("");
      await loadThread(active.patient_user_id);
      await loadThreads();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  }

  return (
    <div className="space-y-8" data-testid="messages-page">
      <Toaster position="top-center" richColors />
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("messages.eyebrow")}</p>
        <h1 className="font-heading text-5xl font-light">{t("messages.title")}</h1>
        <p className="text-stone-500 mt-2">{t("messages.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Threads list */}
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
            <InboxIcon className="w-4 h-4 text-[#B07D4F]" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.25em] text-stone-500">{t("messages.inbox")}</span>
            <span className="ml-auto text-xs text-stone-400">{threads.length}</span>
          </div>
          {threads.length === 0 ? (
            <div className="p-8 text-center text-stone-400 text-sm">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-stone-300" />
              {t("messages.empty")}
            </div>
          ) : (
            <ul className="divide-y divide-stone-100 max-h-[70vh] overflow-y-auto">
              {threads.map((th) => (
                <li key={th.patient_user_id}>
                  <button onClick={() => setActive(th)} data-testid={`thread-${th.patient_user_id}`}
                    className={`w-full text-left px-5 py-4 hover:bg-stone-50 transition-colors ${active?.patient_user_id === th.patient_user_id ? "bg-[#FAF9F6]" : ""}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {th.last_kind === "appointment_booked"
                        ? <CalendarClock className="w-4 h-4 text-[#B07D4F] flex-shrink-0" strokeWidth={1.5} />
                        : th.last_kind === "new_patient"
                          ? <UserPlus className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
                          : <MessageCircle className="w-4 h-4 text-stone-400 flex-shrink-0" strokeWidth={1.5} />}
                      <span className="font-medium text-stone-900 truncate flex-1">{th.patient_name || th.patient_email}</span>
                      {th.unread > 0 && (
                        <span data-testid={`unread-${th.patient_user_id}`}
                          className="text-[10px] font-medium bg-[#D4A373] text-stone-900 px-2 py-0.5 rounded-full">
                          {th.unread}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest mb-1">
                      <span className={th.last_kind === "appointment_booked" ? "text-[#B07D4F]" : "text-stone-400"}>
                        {th.last_kind === "appointment_booked" ? t("messages.typeBooking") : t("messages.typeMessage")}
                      </span>
                    </div>
                    <div className="text-xs text-stone-500 truncate">
                      {th.last_direction === "out" ? "↖ " : ""}
                      {th.last_message}
                    </div>
                    <div className="text-[10px] text-stone-400 mt-1">
                      {th.last_at ? new Date(th.last_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Thread view */}
        <div className="bg-white border border-stone-200 rounded-2xl flex flex-col min-h-[500px]">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-stone-400 p-10 text-center">
              <MessageCircle className="w-12 h-12 mb-4 text-stone-300" strokeWidth={1.25} />
              <p>{t("messages.selectThread")}</p>
            </div>
          ) : (
            <>
              <div className="px-6 py-4 border-b border-stone-100">
                <p className="text-xs uppercase tracking-[0.25em] text-stone-500">{t("messages.conversationWith")}</p>
                <h3 className="font-heading text-xl font-light" data-testid="active-thread-name">{active.patient_name || active.patient_email}</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-[#FAF9F6]/40 max-h-[55vh]">
                {thread.map((m) => (
                  <div key={m.id} data-testid={`msg-${m.id}`}
                    className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                      m.direction === "out"
                        ? "bg-[#D4A373] text-stone-900 rounded-br-sm"
                        : m.kind === "appointment_booked"
                          ? "bg-[#F5EFE6] border border-[#D4A373]/40 text-stone-800 rounded-bl-sm"
                          : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                    }`}>
                      {m.kind === "appointment_booked" && (
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#B07D4F] mb-1.5 font-medium">
                          <CalendarClock className="w-3 h-3" strokeWidth={2} /> {t("messages.typeBooking")}
                        </div>
                      )}
                      <p className="whitespace-pre-wrap break-words text-sm">{m.message}</p>
                      {(m.kind === "document_upload" || m.kind === "appointment_booked") && active?.patient_id && (
                        <button onClick={() => {
                          const tab = m.kind === "document_upload" ? "documentos" : "info";
                          navigate(`/pacientes/${active.patient_id}?tab=${tab}`);
                        }} data-testid={`goto-source-${m.id}`}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#B07D4F] hover:text-[#8a5a2b] font-medium">
                          <ExternalLink className="w-3 h-3" /> {t("messages.goToSource") || "Vai alla scheda"}
                        </button>
                      )}
                      <p className={`text-[10px] mt-1 ${m.direction === "out" ? "text-stone-700" : "text-stone-400"}`}>
                        {new Date(m.created_at).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={listEnd} />
              </div>
              <form onSubmit={sendReply} className="border-t border-stone-100 p-4 flex gap-2">
                <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("messages.replyPlaceholder")}
                  data-testid="reply-input"
                  className="flex-1 px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-full text-sm focus:outline-none focus:border-[#D4A373]" />
                <button type="submit" disabled={sending || !reply.trim()} data-testid="send-reply"
                  className="px-5 py-3 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
